(() => {
  const settingsNode = document.getElementById('deferred-inbox-settings');
  if (!settingsNode) return;

  const settings = JSON.parse(settingsNode.textContent);
  const chatSource = 'https://cdn.shopify.com/storefront/web-components/chat.js';
  const nativeChatPresent = () => document.querySelector(`script[src="${chatSource}"]`);
  let loading;
  let configRequest;

  const getConfig = () => configRequest ||= fetch(settings.configUrl).then(async (response) => {
    if (!response.ok) throw new Error('Could not load Inbox configuration');
    const config = await response.json();
    if (!config.embed?.settings || !Array.isArray(config.page?.translations) || typeof config.css !== 'string') {
      throw new Error('Invalid Inbox configuration');
    }
    return config;
  }).catch((error) => {
    configRequest = null;
    throw error;
  });

  const addStyles = (config) => {
    if (document.getElementById('deferred-inbox-style')) return;
    const style = document.createElement('style');
    style.id = 'deferred-inbox-style';
    style.textContent = config.css + `
      .deferred-inbox-launcher {position:fixed;right:20px;bottom:max(20px,env(safe-area-inset-bottom));z-index:2147483000;display:grid;place-items:center;min-width:50px;height:50px;padding:0 14px;border:0;border-radius:20px;background:color-mix(in srgb,var(--shopify-chat-text-color,#000) 70%,transparent);color:var(--shopify-chat-bg-color,#fff);font:inherit;cursor:pointer}
      .deferred-inbox-launcher svg {width:20px;height:20px;fill:none;stroke:currentColor}
      .deferred-inbox-launcher:focus-visible {outline:2px solid currentColor;outline-offset:3px}
      .deferred-inbox-launcher[aria-busy="true"] {opacity:.65;cursor:wait}
    `;
    document.head.appendChild(style);
  };

  const afterPaint = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  const waitForHero = async () => {
    const hero = document.querySelector('[data-subscription-landing-main] .subscription-main__hero img');
    let observer;
    const heroPainted = new Promise((resolve) => {
      if (!hero || typeof PerformanceObserver === 'undefined') return;
      try {
        observer = new PerformanceObserver((list) => {
          if (list.getEntries().some((entry) => entry.element === hero || (entry.url && entry.url === hero.currentSrc))) resolve();
        });
        observer.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch {}
    });
    if (hero) {
      if (!hero.complete) {
        await new Promise((resolve) => {
          hero.addEventListener('load', resolve, { once: true });
          hero.addEventListener('error', resolve, { once: true });
        });
      }
      try {
        await hero.decode?.();
      } catch {}
    } else if (document.readyState !== 'complete') {
      await new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
    }
    await afterPaint();
    await Promise.race([heroPainted, new Promise((resolve) => setTimeout(resolve, 200))]);
    observer?.disconnect();
  };

  const addData = (id, data) => {
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.type = 'application/json';
    script.id = id;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  };

  const loadChat = (open = false) => {
    if (loading || nativeChatPresent()) return loading;
    loading = (async () => {
      const config = await getConfig();
      if (nativeChatPresent()) return;

      addData('shopify-chat-app-embed-data', config.embed);
      addData('shopify-chat-page-data', config.page);
      addStyles(config);
      if (!document.querySelector('shopify-store[store-domain]')) {
        const store = document.createElement('shopify-store');
        store.setAttribute('store-domain', settings.store);
        store.setAttribute('country', settings.country);
        store.setAttribute('language', settings.language);
        document.body.appendChild(store);
      }
      if (!document.querySelector('shopify-chat')) document.body.appendChild(document.createElement('shopify-chat'));
      if (open) document.querySelector('shopify-chat').setAttribute('open', '');

      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = chatSource;
        script.type = 'module';
        script.crossOrigin = 'anonymous';
        script.fetchPriority = 'low';
        const failed = () => {
          clearTimeout(timer);
          script.remove();
          reject(new Error('Could not load Shopify Inbox'));
        };
        const timer = setTimeout(failed, 15000);
        script.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
        script.addEventListener('error', failed, { once: true });
        document.head.appendChild(script);
      });
      return true;
    })().catch((error) => {
      loading = null;
      console.warn(error.message);
      if (!settings.onDemand) window.addEventListener('online', () => loadChat(open), { once: true });
      return false;
    });
    return loading;
  };

  const showLauncher = async () => {
    try {
      const config = await getConfig();
      if (nativeChatPresent()) return;
      addStyles(config);
      const label = (key) => config.page.translations.find((item) => item.key === `agent_chat.${key}`)?.value || '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'deferred-inbox-launcher';
      button.setAttribute('aria-label', label('panel.open_chat'));
      button.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3.125H11C12.3117 3.125 13.375 4.18832 13.375 5.5V9.09863C13.375 10.3794 12.3591 11.4298 11.0791 11.4727L10.9795 11.4756C10.1532 11.5033 9.38829 11.9188 8.91504 12.5967L8.11621 13.7402C8.06582 13.8125 7.959 13.8116 7.91016 13.7383L7.10352 12.5283C6.62309 11.8078 5.81427 11.375 4.94824 11.375C3.66521 11.375 2.625 10.3348 2.625 9.05176V5.5C2.625 4.18832 3.68832 3.125 5 3.125Z" stroke-width="1.25"/></svg>';
      button.addEventListener('click', async () => {
        try { sessionStorage.setItem('mystearium-inbox-used', 'true'); } catch {}
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-label', label('panel.loading_chat'));
        if (await loadChat(true)) button.remove();
        else {
          button.disabled = false;
          button.setAttribute('aria-busy', 'false');
          button.setAttribute('aria-label', label('error.try_again'));
          button.textContent = label('error.try_again');
        }
      });
      document.body.appendChild(button);
    } catch {
      window.addEventListener('online', showLauncher, { once: true });
    }
  };

  if (nativeChatPresent()) return;
  if (settings.waitForHero) waitForHero().then(() => {
    let restoreOpen = false;
    try { restoreOpen = sessionStorage.getItem('shopify-chat-open') === 'true' || sessionStorage.getItem('mystearium-inbox-used') === 'true'; } catch {}
    if (settings.onDemand && !restoreOpen) showLauncher();
    else loadChat();
  });
  else loadChat();
})();
